import { zodResolver } from "@hookform/resolvers/zod";
import { AddPhotoAlternate, Delete, NotificationsActiveOutlined } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  CircularProgress,
  Divider,
  Grid2,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  SxProps,
  Switch,
  TextField,
  Theme,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { ChangeEvent, ReactNode, useCallback, useEffect, useState } from "react";
import { Controller, FormProvider, useForm, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { listCaptures } from "../../../api/captures";
import { getCaptureById } from "../../../api/captures/capture";
import { useReferenceData } from "../../../hooks";
import { useFormStyles } from "../../../hooks/useFormStyles";
import { EquipmentFormType, getEquipmentFormSchema } from "../../../types/forms/warehouse/equipment";
import { EquipmentResponse } from "../../../types/model/warehouse/equipment";
import { EquipmentImageResponse } from "../../../types/model/warehouse/equipmentImage";
import { UserResponse } from "../../../types/model/user/UserResponse";
import FormAutocomplete from "../../General/FormAutocomplete";
import { PaginatedAutocomplete } from "../../General/PaginatedAutocomplete";
import ImagePreviewDialog from "./ImagePreviewDialog";
import { STATUS_OPTIONS } from "../../../hooks/Warehouse/useEquipmentFilters";
import { formatDMY } from "../../../utils/Format/formatDateWarehouse";

const NO_FILES: File[] = [];

interface SectionProps {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}

const Section = ({ title, action, children }: SectionProps) => (
  <Paper variant="outlined" sx={{ p: 2.5 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
      <Typography variant="subtitle1" fontWeight="medium" sx={{ flexGrow: 1 }}>
        {title}
      </Typography>
      {action}
    </Box>
    {children}
  </Paper>
);

interface FormDateFieldProps {
  name: "damagedDate" | "maintenanceDate";
  label: string;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

// Date as DD/MM/YYYY, form keeps YYYY-MM-DD
const FormDateField = ({ name, label, disabled, sx }: FormDateFieldProps) => {
  const { control } = useFormContext<EquipmentFormType>();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <DatePicker
          label={label}
          format="DD/MM/YYYY"
          value={field.value ? moment(field.value, "YYYY-MM-DD") : null}
          onChange={(d) => field.onChange(d?.isValid() ? d.format("YYYY-MM-DD") : null)}
          disabled={disabled}
          slotProps={{
            textField: {
              fullWidth: true,
              variant: "outlined",
              sx,
              error: !!fieldState.error,
              helperText: fieldState.error?.message,
            },
          }}
        />
      )}
    />
  );
};

interface PreviewState {
  src: string;
  alt: string;
}

interface EquipmentFormProps {
  equipmentData?: EquipmentResponse;
  formId: string;
  isEditing: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  images?: EquipmentImageResponse[];
  isLoadingImages?: boolean;
  isUploadingImage?: boolean;
  deletingImageId?: number | null;
  onUploadImage?: (file: File) => Promise<void>;
  onDeleteImage?: (imageId: number) => Promise<void>;
  pendingImages?: File[];
  onAddPendingImage?: (file: File) => void;
  onRemovePendingImage?: (index: number) => void;
  users?: UserResponse[];
  isLoadingUsers?: boolean;
  onSubmit: (values: EquipmentFormType) => Promise<void>;
}

export default function EquipmentForm({
  equipmentData,
  formId,
  isEditing,
  isSaving,
  images = [],
  isLoadingImages = false,
  isUploadingImage,
  deletingImageId,
  onUploadImage,
  onDeleteImage,
  pendingImages = NO_FILES,
  onAddPendingImage,
  onRemovePendingImage,
  users = [],
  isLoadingUsers = false,
  onSubmit,
}: Readonly<EquipmentFormProps>) {
  const { t } = useTranslation();
  const formStyles = useFormStyles(isEditing);

  const validationSchema = getEquipmentFormSchema(t);
  const defaultFormValues: EquipmentFormType = {
    captureId: equipmentData?.captureId ?? undefined,
    location: equipmentData?.location ?? "",
    equipmentType: equipmentData?.equipmentType ?? "",
    equipmentBrandId: equipmentData?.equipmentBrandId ?? undefined,
    equipmentModel: equipmentData?.equipmentModel ?? "",
    serialNumber: equipmentData?.serialNumber ?? "",
    status: equipmentData?.status ?? "available",
    damagedDate: equipmentData?.damagedDate ?? null,
    maintenanceDate: equipmentData?.maintenanceDate ?? null,
    maintenanceAlertsEnabled: equipmentData?.maintenanceAlertsEnabled ?? false,
    maintenanceAlertDaysBefore: equipmentData?.maintenanceAlertDaysBefore ?? undefined,
    maintenanceAlertIntervalDays: equipmentData?.maintenanceAlertIntervalDays ?? undefined,
    maintenanceNotificationUserIds: equipmentData?.maintenanceNotificationUserIds ?? [],
    notes: equipmentData?.notes ?? "",
  };

  const methods = useForm<EquipmentFormType>({
    resolver: zodResolver(validationSchema),
    defaultValues: defaultFormValues,
  });

  const { data: brands = [] } = useReferenceData("construction", "brands");

  const status = useWatch({ control: methods.control, name: "status" });
  const isDamaged = status === "damaged";

  useEffect(() => {
    if (status !== "damaged") {
      methods.setValue("damagedDate", null);
      methods.clearErrors("damagedDate");
    }
  }, [status, methods]);

  const fetchCaptures = useCallback(
    (search: string, offset: number, limit: number) =>
      listCaptures(undefined, undefined, search, undefined, limit, offset, true),
    [],
  );

  const fetchOneCapture = useCallback((id: number) => getCaptureById(id), []);

  const isFieldDisabled = !isEditing || isSaving;
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [pendingPreviews, setPendingPreviews] = useState<string[]>([]);
  const alertsEnabled = useWatch({ control: methods.control, name: "maintenanceAlertsEnabled" });
  const isAlertFieldDisabled = isFieldDisabled || !alertsEnabled;

  // Pending previews
  useEffect(() => {
    const urls = pendingImages.map((file) => URL.createObjectURL(file));
    setPendingPreviews(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [pendingImages]);

  const handleFormSubmit = async (values: EquipmentFormType) => {
    await onSubmit(values);
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (equipmentData) await onUploadImage?.(file);
    else onAddPendingImage?.(file);
  };

  const imagesCount = images.length + pendingImages.length;

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        id={formId}
        onSubmit={methods.handleSubmit(handleFormSubmit)}
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        {/* Identification */}
        <Section title={t("warehouse.equipment.identification")}>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <PaginatedAutocomplete
                name="captureId"
                label={t("captures.label")}
                fetchFn={fetchCaptures}
                fetchOneFn={fetchOneCapture}
                isEditing={isEditing}
                isSaving={isSaving}
                formStyles={formStyles}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t("warehouse.equipment.location")}
                variant={isEditing ? "outlined" : "standard"}
                disabled={isFieldDisabled}
                sx={{ ...formStyles }}
                {...methods.register("location")}
                error={!!methods.formState.errors.location}
                helperText={methods.formState.errors.location?.message}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label={t("warehouse.equipment.equipment-type")}
                variant={isEditing ? "outlined" : "standard"}
                disabled={isFieldDisabled}
                sx={{ ...formStyles }}
                {...methods.register("equipmentType")}
                error={!!methods.formState.errors.equipmentType}
                helperText={methods.formState.errors.equipmentType?.message}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 4 }}>
              <FormAutocomplete
                name="equipmentBrandId"
                label={t("warehouse.equipment.equipment-brand-id")}
                options={brands}
                isEditing={isEditing}
                isSaving={isSaving}
                disabled={false}
                formStyles={formStyles}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label={t("warehouse.equipment.equipment-model")}
                variant={isEditing ? "outlined" : "standard"}
                disabled={isFieldDisabled}
                sx={{ ...formStyles }}
                {...methods.register("equipmentModel")}
                error={!!methods.formState.errors.equipmentModel}
                helperText={methods.formState.errors.equipmentModel?.message}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t("warehouse.equipment.serial-number")}
                variant={isEditing ? "outlined" : "standard"}
                disabled={isFieldDisabled}
                sx={{ ...formStyles }}
                {...methods.register("serialNumber")}
                error={!!methods.formState.errors.serialNumber}
                helperText={methods.formState.errors.serialNumber?.message}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, md: isDamaged ? 3 : 6 }}>
              <Controller
                name="status"
                control={methods.control}
                render={({ field }) => (
                  <TextField
                    select
                    fullWidth
                    label={t("warehouse.equipment.status")}
                    variant={isEditing ? "outlined" : "standard"}
                    disabled={isFieldDisabled}
                    sx={{ ...formStyles }}
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    error={!!methods.formState.errors.status}
                    helperText={methods.formState.errors.status?.message}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {t(`warehouse.equipment.statuses.${option}`)}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid2>

            {isDamaged && (
              <Grid2 size={{ xs: 12, md: 3 }}>
                {isEditing ? (
                  <FormDateField
                    name="damagedDate"
                    label={t("warehouse.equipment.damaged-date")}
                    disabled={isSaving}
                    sx={{ ...formStyles }}
                  />
                ) : (
                  <TextField
                    fullWidth
                    label={t("warehouse.equipment.damaged-date")}
                    variant="standard"
                    disabled
                    value={formatDMY(equipmentData?.damagedDate)}
                    sx={{ ...formStyles }}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              </Grid2>
            )}
          </Grid2>
        </Section>

        {/* Images */}
        <Section
          title={t("warehouse.equipment.images")}
          action={
            <Typography variant="caption" color="text.secondary">
              {imagesCount}
            </Typography>
          }
        >
          {isLoadingImages ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              {imagesCount === 0 && !isEditing && (
                <Typography variant="body2" color="text.secondary">
                  {t("warehouse.equipment.no-images")}
                </Typography>
              )}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                  gap: 2,
                }}
              >
                {images.map((image) => {
                  const alt = `${equipmentData?.id}-${image.id}`;
                  return (
                    <Box key={image.id} sx={{ position: "relative" }}>
                      <Box
                        component="img"
                        src={image.url}
                        alt={alt}
                        onClick={() => setPreview({ src: image.url, alt })}
                        sx={{
                          width: "100%",
                          aspectRatio: "4 / 3",
                          objectFit: "cover",
                          borderRadius: 1,
                          border: 1,
                          borderColor: "divider",
                          display: "block",
                          cursor: "zoom-in",
                        }}
                      />
                      {isEditing && (
                        <IconButton
                          size="small"
                          color="error"
                          disabled={deletingImageId === image.id}
                          onClick={() => onDeleteImage?.(image.id)}
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            bgcolor: "background.paper",
                            "&:hover": { bgcolor: "background.paper" },
                          }}
                        >
                          {deletingImageId === image.id ? <CircularProgress size={16} /> : <Delete fontSize="small" />}
                        </IconButton>
                      )}
                    </Box>
                  );
                })}

                {/* Pending images */}
                {pendingPreviews.map((src, index) => {
                  const alt = pendingImages[index]?.name ?? "";
                  return (
                    <Box key={src} sx={{ position: "relative" }}>
                      <Box
                        component="img"
                        src={src}
                        alt={alt}
                        onClick={() => setPreview({ src, alt })}
                        sx={{
                          width: "100%",
                          aspectRatio: "4 / 3",
                          objectFit: "cover",
                          borderRadius: 1,
                          border: 1,
                          borderColor: "divider",
                          display: "block",
                          cursor: "zoom-in",
                        }}
                      />
                      <IconButton
                        size="small"
                        color="error"
                        disabled={isSaving}
                        onClick={() => onRemovePendingImage?.(index)}
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          bgcolor: "background.paper",
                          "&:hover": { bgcolor: "background.paper" },
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                })}

                {isEditing && (
                  <Box
                    component="label"
                    sx={{
                      aspectRatio: "4 / 3",
                      border: "2px dashed",
                      borderColor: "divider",
                      borderRadius: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      color: "text.secondary",
                      cursor: "pointer",
                      pointerEvents: isUploadingImage || isSaving ? "none" : "auto",
                      opacity: isUploadingImage ? 0.6 : 1,
                      "&:hover": { borderColor: "primary.main", color: "primary.main" },
                    }}
                  >
                    {isUploadingImage ? <CircularProgress size={28} /> : <AddPhotoAlternate fontSize="large" />}
                    <Typography variant="body2">{t("warehouse.equipment.add-image")}</Typography>
                    <input hidden type="file" accept="image/*" onChange={handleFileChange} />
                  </Box>
                )}
              </Box>
            </>
          )}
        </Section>

        {/* Maintenance */}
        <Section title={t("warehouse.equipment.maintenance")}>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, md: 6 }}>
              {isEditing ? (
                <FormDateField
                  name="maintenanceDate"
                  label={t("warehouse.equipment.maintenance-date")}
                  disabled={isSaving}
                  sx={{ ...formStyles }}
                />
              ) : (
                <TextField
                  fullWidth
                  label={t("warehouse.equipment.maintenance-date")}
                  variant="standard"
                  disabled
                  value={formatDMY(equipmentData?.maintenanceDate)}
                  sx={{ ...formStyles }}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              )}
            </Grid2>

            {equipmentData && (
              <Grid2 size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t("warehouse.equipment.last-maintenance-alert-on")}
                  variant="standard"
                  disabled
                  value={formatDMY(equipmentData.lastMaintenanceAlertOn)}
                />
              </Grid2>
            )}

            <Grid2 size={12}>
              <Divider />
            </Grid2>

            {/* Alerts */}
            <Grid2 size={12}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <NotificationsActiveOutlined color={alertsEnabled ? "primary" : "disabled"} fontSize="small" />
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {t("warehouse.equipment.maintenance-alerts-enabled")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("warehouse.equipment.maintenance-alerts-description")}
                    </Typography>
                  </Box>
                </Box>
                <Controller
                  name="maintenanceAlertsEnabled"
                  control={methods.control}
                  render={({ field }) => (
                    <Switch
                      checked={!!field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      disabled={isFieldDisabled}
                    />
                  )}
                />
              </Box>
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label={t("warehouse.equipment.maintenance-alert-days-before")}
                variant={isEditing ? "outlined" : "standard"}
                disabled={isAlertFieldDisabled}
                sx={{ ...formStyles }}
                slotProps={{
                  htmlInput: { min: 0 },
                  input: { endAdornment: <InputAdornment position="end">{t("generic.units.days")}</InputAdornment> },
                }}
                {...methods.register("maintenanceAlertDaysBefore", {
                  setValueAs: (v) => (v === "" || v == null ? undefined : Number(v)),
                })}
                error={!!methods.formState.errors.maintenanceAlertDaysBefore}
                helperText={methods.formState.errors.maintenanceAlertDaysBefore?.message}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label={t("warehouse.equipment.maintenance-alert-interval-days")}
                variant={isEditing ? "outlined" : "standard"}
                disabled={isAlertFieldDisabled}
                sx={{ ...formStyles }}
                slotProps={{
                  htmlInput: { min: 1 },
                  input: { endAdornment: <InputAdornment position="end">{t("generic.units.days")}</InputAdornment> },
                }}
                {...methods.register("maintenanceAlertIntervalDays", {
                  setValueAs: (v) => (v === "" || v == null ? undefined : Number(v)),
                })}
                error={!!methods.formState.errors.maintenanceAlertIntervalDays}
                helperText={methods.formState.errors.maintenanceAlertIntervalDays?.message}
              />
            </Grid2>

            <Grid2 size={12}>
              <Controller
                name="maintenanceNotificationUserIds"
                control={methods.control}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    options={users}
                    loading={isLoadingUsers}
                    disabled={isAlertFieldDisabled}
                    value={users.filter((u) => field.value?.includes(u.id))}
                    onChange={(_, selected) => field.onChange(selected.map((u) => u.id))}
                    getOptionLabel={(u) => u.name ?? u.email}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("warehouse.equipment.maintenance-notification-users")}
                        variant={isEditing ? "outlined" : "standard"}
                        sx={{ ...formStyles }}
                        error={!!methods.formState.errors.maintenanceNotificationUserIds}
                        helperText={methods.formState.errors.maintenanceNotificationUserIds?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid2>
          </Grid2>
        </Section>

        {/* Notes */}
        <Section title={t("warehouse.equipment.notes")}>
          <TextField
            fullWidth
            placeholder={t("warehouse.equipment.notes-placeholder")}
            multiline
            minRows={4}
            variant={isEditing ? "outlined" : "standard"}
            disabled={isFieldDisabled}
            sx={{ ...formStyles }}
            {...methods.register("notes")}
            error={!!methods.formState.errors.notes}
            helperText={methods.formState.errors.notes?.message}
          />
        </Section>

        <ImagePreviewDialog
          open={!!preview}
          src={preview?.src}
          alt={preview?.alt}
          onClose={() => setPreview(null)}
        />
      </Box>
    </FormProvider>
  );
}